import { Injectable, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { format } from 'date-fns/format';
import { filter, tap } from 'rxjs';

import { AuthService } from '../../auth';
import {
  RoomDetailsModel,
  RoomLayoutsModel,
  RoomServicesModel,
} from '../../rooms';
import {
  RoomServicesApiResponseEnum,
  Service,
} from '../model/booking-request-api.model';
import {
  BookingRequestBaseModel,
  BookingRequestDetailsModel,
} from '../model/booking-request.model';
import {
  MAX_ON_BEHALF_PERSONS,
  OnBehalfFormArrayModel,
  RoomBookingFormModel,
  RoomBookingFormValueModel,
} from '../model/room-booking-form.model';
import { blockingTimeValidator } from '../validators/blocking-time.validator';
import {
  durationToMinutes,
  eventDurationValidator,
} from '../validators/event-duration.validator';
import {
  dateBeforeAYearValidator,
  eventTimeValidator,
} from '../validators/event-time.validator';
import {
  onBehalfNotRequesterValidator,
  uniqueOnBehalfValidator,
} from '../validators/on-behalf.validator';

// provide this service in the component
@Injectable()
export class RoomBookingFormService {
  readonly #fb = inject(FormBuilder);
  readonly #authService = inject(AuthService);

  readonly #isEditMode = signal(false);
  readonly #unavailableServices = signal<Service[]>([]);
  readonly #roomBookingForm: RoomBookingFormModel = this.#fb.group(
    {
      eventName: this.#fb.nonNullable.control<string>('', [
        Validators.required,
      ]),
      startDate: this.#fb.nonNullable.control<Date>(new Date(), [
        Validators.required,
      ]),
      endDate: this.#fb.nonNullable.control<Date>(new Date(), [
        Validators.required,
      ]),
      startHour: this.#fb.control<Date | null>(null, [Validators.required]),
      endHour: this.#fb.control<Date | null>(null, [Validators.required]),
      wbsCode: this.#fb.control<string>(''),
      costCenter: this.#fb.nonNullable.control<string>(
        this.#authService.costCenter,
        [
          Validators.required,
          Validators.minLength(10),
          Validators.maxLength(10),
          Validators.pattern('^[a-zA-Z0-9]*$'),
        ]
      ),
      numberOfPeople: this.#fb.nonNullable.control<number>(0, [
        Validators.required,
        Validators.min(1),
      ]),
      roomLayoutUUID: this.#fb.control<RoomLayoutsModel | null>(null),
      onBehalfEmails: this.#fb.array<FormControl<string | null>>(
        [],
        uniqueOnBehalfValidator() as ValidatorFn
      ),
      cecOnSite: this.#fb.control<boolean | null>(false),
      hcpOnSite: this.#fb.control<boolean | null>(false),
      moreThan20Externals: this.#fb.control<boolean | null>(false),
      customSetupTimeBeforeM: this.#fb.control<number | null>(null, [
        Validators.min(0),
        Validators.max(59),
      ]),
      customSetupTimeBeforeH: this.#fb.control<number | null>(null, [
        Validators.min(0),
      ]),
      customSetupTimeAfterM: this.#fb.control<number | null>(null, [
        Validators.min(0),
        Validators.max(59),
      ]),
      customSetupTimeAfterH: this.#fb.control<number | null>(null, [
        Validators.min(0),
      ]),
      additionalInfo: this.#fb.control<string | null>(null),
    },
    { validators: [eventTimeValidator() as ValidatorFn] }
  );

  readonly #roomBookingServicesForm = this.#fb.group({});
  #availableServices: RoomServicesModel[] = [];
  #defaultLayoutId: string | undefined = undefined;
  // whoever requests the booking - the logged in user while creating one, the
  // original requester while editing an existing one
  #requesterEmail: string | null = this.#authService.user?.email ?? null;

  get roomBookingForm() {
    return this.#roomBookingForm;
  }

  get roomBookingServicesForm() {
    return this.#roomBookingServicesForm;
  }

  get onBehalfEmails(): OnBehalfFormArrayModel {
    return this.#roomBookingForm.controls.onBehalfEmails;
  }

  get canAddOnBehalfPerson(): boolean {
    return (
      this.onBehalfEmails.enabled &&
      this.onBehalfEmails.length < MAX_ON_BEHALF_PERSONS
    );
  }

  get isEditMode() {
    return this.#isEditMode.asReadonly();
  }

  get unavailableServices() {
    return this.#unavailableServices.asReadonly();
  }

  get layoutsControlValueChange() {
    return this.#roomBookingForm.controls.roomLayoutUUID.valueChanges.pipe(
      filter(Boolean),
      tap(selectedLayout => {
        this.#roomBookingForm.controls.numberOfPeople.setValidators([
          Validators.min(selectedLayout.capacity.min),
          Validators.max(selectedLayout.capacity.max),
        ]);
        this.#roomBookingForm.controls.numberOfPeople.updateValueAndValidity();
        if (selectedLayout.layoutDexUUID !== this.#defaultLayoutId) {
          if (this.#roomBookingServicesForm.get('FURNITURE')) {
            const serviceControl = this.#roomBookingServicesForm.get(
              'FURNITURE'
            ) as unknown as FormControl<boolean>;
            if (serviceControl) {
              serviceControl.patchValue(true, { emitEvent: false });
            }
          }
        }
      })
    );
  }

  public setFormValues(bookingDetails: BookingRequestDetailsModel) {
    this.#roomBookingForm.patchValue({
      eventName: bookingDetails.eventName,
      startDate: bookingDetails.startTime,
      endDate: bookingDetails.endTime,
      startHour: bookingDetails.startTime,
      endHour: bookingDetails.endTime,
      wbsCode: bookingDetails.wbsCode,
      costCenter: bookingDetails.costCenter,
      numberOfPeople: bookingDetails.numberOfPeople,
      cecOnSite: bookingDetails.generalQuestions.cecOnSite,
      hcpOnSite: bookingDetails.generalQuestions.hcpOnSite,
      moreThan20Externals: bookingDetails.generalQuestions.moreThan20Externals,
      customSetupTimeBeforeM: this.minutesToHoursMinutes(
        bookingDetails.customSetupTimeBefore
      ).minutes,
      customSetupTimeBeforeH: this.minutesToHoursMinutes(
        bookingDetails.customSetupTimeBefore
      ).hours,
      customSetupTimeAfterH: this.minutesToHoursMinutes(
        bookingDetails.customSetupTimeAfter
      ).hours,
      customSetupTimeAfterM: this.minutesToHoursMinutes(
        bookingDetails.customSetupTimeAfter
      ).minutes,
      additionalInfo: bookingDetails.additionalInfo,
    });

    this.#requesterEmail =
      bookingDetails.requester?.email ?? this.#authService.user?.email ?? null;
    this.setOnBehalfPersons(
      bookingDetails.onBehalfParticipants?.map(
        participant => participant.email
      ) ?? []
    );

    const unavailableServices: Service[] = [];

    bookingDetails.services?.forEach(service => {
      if (this.#roomBookingServicesForm.get(service.serviceType)) {
        const serviceControl = this.#roomBookingServicesForm.get(
          service.serviceType
        ) as unknown as FormControl<boolean>;
        if (serviceControl) {
          serviceControl.patchValue(true, { emitEvent: false });
        }
      } else {
        unavailableServices.push(service);
      }
    });

    this.#unavailableServices.set(unavailableServices);
  }

  public addOnBehalfPerson(email: string | null = null) {
    if (this.onBehalfEmails.length >= MAX_ON_BEHALF_PERSONS) {
      return;
    }
    this.onBehalfEmails.push(this.#createOnBehalfControl(email));
  }

  public removeOnBehalfPerson(index: number) {
    this.onBehalfEmails.removeAt(index);
  }

  public setOnBehalfPersons(emails: (string | null)[]) {
    this.onBehalfEmails.clear({ emitEvent: false });
    emails.slice(0, MAX_ON_BEHALF_PERSONS).forEach(email =>
      this.onBehalfEmails.push(this.#createOnBehalfControl(email), {
        emitEvent: false,
      })
    );
    this.onBehalfEmails.updateValueAndValidity();
  }

  public clearOnBehalfPersons() {
    this.onBehalfEmails.clear();
    this.onBehalfEmails.updateValueAndValidity();
  }

  #createOnBehalfControl(email: string | null = null) {
    const control = this.#fb.control<string | null>(email, [
      Validators.required,
      Validators.email,
      onBehalfNotRequesterValidator(() => this.#requesterEmail),
    ]);
    // a control added to an already disabled form would otherwise re-enable it
    if (this.#roomBookingForm.disabled) {
      control.disable({ emitEvent: false });
    }
    return control;
  }

  public setServicesControls(availableServices: RoomServicesModel[]) {
    availableServices.forEach(service => {
      const serviceControl = this.#fb.nonNullable.control(
        false
      ) as FormControl<boolean>;

      this.#roomBookingServicesForm.addControl(service.type, serviceControl, {
        emitEvent: false,
      });
    });
    this.#availableServices = availableServices;
  }

  public setEventDateTime(
    date: Date,
    roomDetails: RoomDetailsModel | null,
    duration?: number | null
  ) {
    const endDate = new Date(date);
    const durationMinimum =
      roomDetails?.manageSpaceConfiguration?.durationRange?.min;
    const defaultDuration = duration
      ? duration
      : durationMinimum && durationToMinutes(durationMinimum) > 0
        ? durationToMinutes(durationMinimum)
        : 60;
    endDate.setMinutes(date.getMinutes() + defaultDuration);
    this.#roomBookingForm.patchValue({
      startDate: date,
      startHour: date,
      endHour: endDate,
      endDate: endDate,
    });
  }

  public setEditMode(isEditMode: boolean) {
    if (isEditMode) {
      this.#roomBookingForm.enable();
      this.#roomBookingServicesForm.enable();
      this.#roomBookingServicesForm.markAllAsTouched();
      this.#roomBookingForm.markAllAsTouched();
    } else {
      this.#roomBookingServicesForm.disable();
      this.#roomBookingForm.disable();
    }
    this.#isEditMode.set(isEditMode);
  }

  public setRoomDataValidators(roomDetails: RoomDetailsModel) {
    this.#roomBookingForm.controls.numberOfPeople.addValidators([
      Validators.max(roomDetails.capacity),
      Validators.min(roomDetails.minCapacity),
    ]);

    const min = roomDetails.manageSpaceConfiguration?.durationRange?.min;
    const max = roomDetails.manageSpaceConfiguration?.durationRange?.max;
    if (min && max) {
      this.#roomBookingForm.addValidators(
        eventDurationValidator(min, max) as ValidatorFn
      );
    }
    const blockingTimes = roomDetails.manageSpaceConfiguration?.blockingTimes;
    if (blockingTimes && blockingTimes.length > 0) {
      this.#roomBookingForm.addValidators(
        blockingTimeValidator(blockingTimes) as ValidatorFn
      );
    }
    this.#roomBookingForm.updateValueAndValidity();
  }

  public setRoomLayoutsValidators(
    roomLayouts: RoomLayoutsModel[],
    layoutId?: string
  ) {
    this.#defaultLayoutId = layoutId;

    if (roomLayouts.length === 0) {
      this.#roomBookingForm.controls.roomLayoutUUID.clearValidators();
      this.#roomBookingForm.controls.roomLayoutUUID.patchValue(null);
      this.#roomBookingForm.controls.roomLayoutUUID.updateValueAndValidity();
      return;
    }

    const selectedLayout = roomLayouts.find(
      layout => layout.layoutDexUUID === layoutId
    );
    this.#roomBookingForm.controls.roomLayoutUUID.addValidators([
      this.#activeLayoutValidator(),
    ]);
    if (selectedLayout) {
      this.#roomBookingForm.controls.roomLayoutUUID.patchValue(selectedLayout);
      this.#roomBookingForm.controls.roomLayoutUUID.markAsTouched();
      this.#roomBookingForm.controls.roomLayoutUUID.updateValueAndValidity();
      this.#roomBookingForm.controls.numberOfPeople.setValidators([
        Validators.min(selectedLayout.capacity.min),
        Validators.max(selectedLayout.capacity.max),
      ]);
      this.#roomBookingForm.controls.numberOfPeople.markAsTouched();
      this.#roomBookingForm.controls.numberOfPeople.updateValueAndValidity();
    }
  }

  #activeLayoutValidator(): ValidatorFn {
    return control => {
      const layout = control.value as RoomLayoutsModel | null;
      if (layout && !layout.active) {
        return { inactiveLayout: true };
      }
      return null;
    };
  }

  public createRoomBookingRequestBody(): BookingRequestBaseModel {
    const formValue =
      this.#roomBookingForm.getRawValue() as RoomBookingFormValueModel;
    const servicesFormValue: { [key: string]: boolean } =
      this.#roomBookingServicesForm.value;
    const startTime = `${format(formValue.startDate, 'yyyy-MM-dd')}T${format(formValue.startHour as Date, 'HH:mm')}:00+00:00`;
    const endTime = `${format(formValue.endDate, 'yyyy-MM-dd')}T${format(formValue.endHour as Date, 'HH:mm')}:00+00:00`;
    const services: { serviceType: RoomServicesApiResponseEnum }[] = [];
    const generalQuestions = {
      cecOnSite: formValue.cecOnSite as boolean,
      hcpOnSite: formValue.hcpOnSite as boolean,
      moreThan20Externals: formValue.moreThan20Externals as boolean,
    };
    const onBehalfEmails = formValue.onBehalfEmails.filter(
      (email): email is string => !!email
    );

    this.#availableServices.forEach(service => {
      if (servicesFormValue[service.type]) {
        services.push({ serviceType: service.type });
      }
    });

    return {
      eventName: formValue.eventName,
      numberOfPeople: formValue.numberOfPeople,
      wbsCode: formValue.wbsCode,
      costCenter: formValue.costCenter,
      startTime,
      endTime,
      roomLayoutUUID: formValue.roomLayoutUUID
        ? formValue.roomLayoutUUID.layoutDexUUID
        : null,
      services,
      onBehalfEmails,
      generalQuestions: generalQuestions,
      customSetupTimeBefore: this.hoursMinutesToMinutes(
        formValue.customSetupTimeBeforeH,
        formValue.customSetupTimeBeforeM
      ),
      customSetupTimeAfter: this.hoursMinutesToMinutes(
        formValue.customSetupTimeAfterH,
        formValue.customSetupTimeAfterM
      ),
      additionalInfo: formValue.additionalInfo,
    };
  }

  public addOneYearConstraint() {
    this.#roomBookingForm.addValidators(
      dateBeforeAYearValidator() as ValidatorFn
    );
    this.#roomBookingForm.controls.startDate.markAsTouched();
  }

  protected minutesToHoursMinutes(totalMinutes: number | null | undefined): {
    hours: number | null;
    minutes: number | null;
  } {
    if (totalMinutes !== null && totalMinutes !== undefined) {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return { hours, minutes };
    } else {
      return { hours: null, minutes: null };
    }
  }

  protected hoursMinutesToMinutes(
    hours: number | null,
    minutes: number | null
  ): number | null {
    if (hours === null && minutes === null) {
      return null;
    }
    if (
      (hours === null && minutes === 0) ||
      (minutes === null && hours === 0)
    ) {
      return 0;
    }
    return (hours ?? 0) * 60 + (minutes ?? 0);
  }
}
